import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { ICompany } from '@nxsys/types/models/company';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { IRELAND } from '@nxsys/store/constants/keys';
import { formatPostCode, postCodeValidationRule } from '@nxsys/utils/formFieldValidation';
import { Form, Row, Col } from 'antd';
import { useImperativeHandle, Ref, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export type ContactAndPaymentFormRef = {
  submit: () => void;
};

export type ContactAndPaymentFormValues = {
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  eircode: string;
  phone: string;
  bankName: string;
  bankBranch: string;
  bankSortCode: string;
  accountNumber: string;
  bankAccountName: string;
  bankReference: string;
  iban: string;
  bic: string;
};

type ContactAndPaymentFormProps = {
  companyData?: ICompany;
  formRef?: Ref<ContactAndPaymentFormRef>;
  onSubmit?: (values: ContactAndPaymentFormValues) => void;
};

const ContactAndPaymentForm: React.FC<ContactAndPaymentFormProps> = ({
  companyData,
  formRef,
  onSubmit,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const [form] = Form.useForm();
  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
  }));

  const onFinish = (values: any) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  useEffect(() => {
    if (companyData && needReinitializeForm) {
      const initialValues = {
        address1: companyData.pensionSetting?.address[0] || '',
        address2: companyData.pensionSetting?.address[1] || '',
        address3: companyData.pensionSetting?.address[2] || '',
        address4: companyData.pensionSetting?.address[3] || '',
        eircode: companyData.pensionSetting?.eirCode || '',
        phone: companyData.pensionSetting?.phone || '',
        bankName: companyData.pensionSetting?.bankName || '',
        bankBranch: companyData.pensionSetting?.bankBranch || '',
        bankSortCode: companyData.pensionSetting?.bankSortCode || '',
        accountNumber: companyData.pensionSetting?.bankAccountNumber || '',
        bankAccountName: companyData.pensionSetting?.bankAccountName || '',
        bankReference: companyData.pensionSetting?.bankReference || '',
        iban: companyData.pensionSetting?.IBAN || '',
        bic: companyData.pensionSetting?.BIC || '',
      };
      form.setFieldsValue(initialValues);
      setNeedReinitializeForm(false);
    }
  }, [companyData]);

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <Row gutter={24}>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.address1']}
            name="address1"
          >
            <AppInput placeholder={messages['common.addressLine1']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.address2']}
            name="address2"
          >
            <AppInput placeholder={messages['common.addressLine2']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.address3']}
            name="address3"
          >
            <AppInput placeholder={messages['common.addressLine3']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.address4']}
            name="address4"
          >
            <AppInput placeholder={messages['common.addressLine4']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={payrollCountry === IRELAND ? messages['common.eircode'] : messages['common.postCode']}
            name="eircode"
            rules={[postCodeValidationRule(messages, payrollCountry)]}
          >
            <AppInput
              placeholder={payrollCountry === IRELAND ? messages['common.eircode'] : messages['common.postCode']}
              upperCaseAll
              onChange={(e) => {
                form.setFieldValue('eircode', formatPostCode(e.target.value, payrollCountry));
              }}
              maxLength={8}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.phone']} name="phone">
            <AppInput placeholder={messages['common.phone']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.bankName']}
            name="bankName"
          >
            <AppInput placeholder={messages['common.bankName']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.bankBranch']}
            name="bankBranch"
          >
            <AppInput placeholder={messages['common.bankBranch']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.bankSortCode']}
            name="bankSortCode"
          >
            <AppInput placeholder={messages['common.bankSortCode']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.accountNumber']}
            name="accountNumber"
          >
            <AppInput placeholder={messages['common.accountNumber']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.bankAccountName']}
            name="bankAccountName"
          >
            <AppInput placeholder={messages['common.bankAccountName']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.bankReference']}
            name="bankReference"
          >
            <AppInput placeholder={messages['common.bankReference']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.iban']} name="iban">
            <AppInput placeholder={messages['common.iban']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.bic']} name="bic">
            <AppInput placeholder={messages['common.bic']} />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default ContactAndPaymentForm;
